// Car radio: five stations of generated music (no audio files), a "song" every ~75 s with a
// made-up title, DJ chatter that knows what's pumping, and a burst of static when you tune.
// Plays only while you're in a vehicle; ducks the district music while it's on.
import { signal } from "@preact/signals";
import { uiPrefs } from "./prefs";
import { duckMusic } from "./music";

type Genre = "afro" | "piano" | "lofi" | "trap" | "highlife";
interface Station {
  name: string;
  freq: string;
  genre: Genre;
  bpm: number;
  color: string;
  dj: string[];
}

export const STATIONS: Station[] = [
  { name: "Degen FM", freq: "101.5", genre: "afro", bpm: 104, color: "#22c55e", dj: ["gm gm Degen Island! Windows down, bags up.", "This one goes out to everyone still holding. Respect.", "Shoutout to the Danfo drivers. Una dey try!"] },
  { name: "Amapiano Wave", freq: "98.7", genre: "piano", bpm: 112, color: "#a855f7", dj: ["Log drum check... we're live!", "If your coin is pumping, turn this up.", "Late night on the expressway, just vibes."] },
  { name: "Lofi Pump", freq: "88.1", genre: "lofi", bpm: 78, color: "#38bdf8", dj: ["Beats to watch charts to.", "Breathe. Zoom out. It's a long game.", "Rainy night, red candles, cozy beats."] },
  { name: "Ape Trap", freq: "107.9", genre: "trap", bpm: 140, color: "#ef4444", dj: ["APE RADIO. No stop losses. No regrets.", "If you're not up 10x, are you even trying?", "Send it. Full port. (Not financial advice.)"] },
  { name: "Gm Highlife", freq: "93.3", genre: "highlife", bpm: 120, color: "#f59e0b", dj: ["Good morning from the coast road!", "Old school sweetness for your road trip.", "Smile, you're on Degen Island."] },
];

const TITLES_A = ["Gas Fee", "Exit Liquidity", "Diamond Hands", "Wagmi", "Rug Me", "Ser Please", "Moon Mission", "Bonding Curve", "Airdrop", "Lekki Night", "Bag Holder", "Green Candle", "Paper Hands", "Danfo"];
const TITLES_B = ["Blues", "Love", "Anthem", "Softly", "(Lagos Mix)", "Remix", "Freestyle", "Riddim", "Dreams", "Season", "Vibes", "Interlude"];
const ARTISTS = ["DJ Rugpull", "Lil Bags", "Kemi Ether", "Tunde Gwei", "Ada Liquidity", "MC Wagmi", "Burna Bullrun", "Satoshi Sound", "The Paper Hands", "Mama Mempool"];

export interface RadioState {
  on: boolean;
  station: number;
  title: string;
  artist: string;
  dj: string;
}
export const radio = signal<RadioState>({ on: false, station: 0, title: "", artist: "", dj: "" });

const KEY = "degen-world:radio";
function saved(): { on: boolean; station: number } {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "null");
    if (v && typeof v.station === "number") return { on: !!v.on, station: Math.abs(v.station) % STATIONS.length };
  } catch {
    /* storage blocked: defaults */
  }
  return { on: false, station: 0 };
}
function save() {
  try {
    localStorage.setItem(KEY, JSON.stringify({ on: radio.value.on, station: radio.value.station }));
  } catch {
    /* fine */
  }
}

// ---------------------------------------------------------------- audio
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let noise: AudioBuffer | null = null;
let crackle: AudioBufferSourceNode | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let nextT = 0;
let step = 0;
let inCar = false;
let song = { seed: 1, root: 0, prog: [0, 0, 0, 0], mel: [] as (number | null)[], bars: 32 };
let news: () => string[] = () => [];

function ensure(): AudioContext | null {
  if (ctx) return ctx;
  try {
    ctx = new AudioContext();
  } catch {
    return null;
  }
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -16;
  comp.ratio.value = 4;
  master = ctx.createGain();
  master.gain.value = 0;
  master.connect(comp).connect(ctx.destination);
  noise = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const d = noise.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return ctx;
}

const hz = (midi: number) => 440 * 2 ** ((midi - 69) / 12);

function env(g: GainNode, t: number, peak: number, attack: number, decay: number) {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
}

function tone(t: number, f: number, len: number, type: OscillatorType, vol: number, opt: { attack?: number; lp?: number; glideTo?: number; drop?: number } = {}) {
  const c = ctx!;
  const o = c.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(f * (opt.drop ?? 1), t);
  if (opt.drop) o.frequency.exponentialRampToValueAtTime(f, t + 0.06);
  if (opt.glideTo) o.frequency.exponentialRampToValueAtTime(opt.glideTo, t + len * 0.8);
  const g = c.createGain();
  env(g, t, vol, opt.attack ?? 0.005, len);
  let node: AudioNode = o;
  if (opt.lp) {
    const f2 = c.createBiquadFilter();
    f2.type = "lowpass";
    f2.frequency.value = opt.lp;
    o.connect(f2);
    node = f2;
  }
  node.connect(g).connect(master!);
  o.start(t);
  o.stop(t + (opt.attack ?? 0.005) + len + 0.05);
}

function hit(t: number, len: number, vol: number, type: BiquadFilterType, freq: number, q = 1) {
  const c = ctx!;
  const s = c.createBufferSource();
  s.buffer = noise;
  s.playbackRate.value = 0.8 + Math.random() * 0.4;
  const f = c.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  f.Q.value = q;
  const g = c.createGain();
  env(g, t, vol, 0.002, len);
  s.connect(f).connect(g).connect(master!);
  s.start(t, Math.random() * 1.5);
  s.stop(t + len + 0.05);
}

const kick = (t: number, v = 0.9) => {
  const c = ctx!;
  const o = c.createOscillator();
  o.frequency.setValueAtTime(150, t);
  o.frequency.exponentialRampToValueAtTime(42, t + 0.13);
  const g = c.createGain();
  env(g, t, v, 0.002, 0.32);
  o.connect(g).connect(master!);
  o.start(t);
  o.stop(t + 0.4);
};
const snare = (t: number, v = 0.35) => {
  hit(t, 0.16, v, "bandpass", 1800, 0.8);
  tone(t, 190, 0.08, "triangle", v * 0.5);
};
const hat = (t: number, v = 0.12, open = false) => hit(t, open ? 0.22 : 0.04, v, "highpass", 7500);
const shaker = (t: number, v = 0.08) => hit(t, 0.05, v, "bandpass", 5200, 2);
const rim = (t: number, v = 0.18) => hit(t, 0.03, v, "bandpass", 3200, 6);
const logDrum = (t: number, f: number, v = 0.5) => tone(t, f, 0.32, "sine", v, { drop: 1.6, lp: 900 });
const pluck = (t: number, f: number, v = 0.14) => {
  tone(t, f, 0.22, "triangle", v, { lp: 2600 });
  tone(t, f * 4, 0.06, "sine", v * 0.25);
};
const keys = (t: number, notes: number[], len: number, v = 0.05) => {
  for (const m of notes) {
    tone(t, hz(m), len, "sine", v, { attack: 0.02, lp: 1800 });
    tone(t, hz(m) * 1.003, len, "triangle", v * 0.5, { attack: 0.02, lp: 1400 });
  }
};
const bell = (t: number, f: number, v = 0.08) => {
  tone(t, f, 0.6, "sine", v);
  tone(t, f * 2.76, 0.25, "sine", v * 0.35);
};

// Minor-ish progressions as scale degrees in semitones from the root.
const PROGS = [
  [0, 8, 3, 10],
  [0, 5, 8, 7],
  [9, 5, 0, 7],
  [0, 3, 8, 7],
  [2, 7, 0, 5],
];
const triad = (root: number, minor: boolean) => [root, root + (minor ? 3 : 4), root + 7];

function newSong() {
  const st = STATIONS[radio.value.station];
  const seed = (Math.random() * 2 ** 31) | 0;
  let s = seed;
  const r = () => ((s = (Math.imul(s, 1103515245) + 12345) >>> 0) / 4294967296);
  const scale = st.genre === "highlife" ? [0, 2, 4, 7, 9, 12, 14] : [0, 3, 5, 7, 10, 12, 15];
  song = {
    seed,
    root: 48 + Math.floor(r() * 7),
    prog: PROGS[Math.floor(r() * PROGS.length)],
    mel: Array.from({ length: 16 }, () => (r() < 0.45 ? scale[Math.floor(r() * scale.length)] : null)),
    bars: 28 + Math.floor(r() * 8),
  };
  const lines = [...st.dj, ...news()];
  radio.value = {
    ...radio.value,
    title: `${TITLES_A[Math.floor(r() * TITLES_A.length)]} ${TITLES_B[Math.floor(r() * TITLES_B.length)]}`,
    artist: ARTISTS[Math.floor(r() * ARTISTS.length)],
    dj: lines[Math.floor(r() * lines.length)] ?? "",
  };
}

/** Schedule one 16th-note step for the current station. */
function playStep(t: number, i: number) {
  const st = STATIONS[radio.value.station];
  const s16 = i % 16;
  const bar = Math.floor(i / 16);
  const chordRoot = song.root + song.prog[bar % 4];
  const minor = st.genre !== "highlife";
  const mel = song.mel[s16];
  const lead = mel === null ? null : hz(song.root + 24 + mel);
  switch (st.genre) {
    case "afro": // Afrobeats: syncopated kick, rim on the "and", shaker 16ths, bouncy bass, marimba lead
      if ([0, 7, 10].includes(s16)) kick(t, 0.8);
      if ([4, 12].includes(s16)) snare(t, 0.22);
      if ([3, 6, 11, 14].includes(s16)) rim(t);
      shaker(t, s16 % 2 ? 0.05 : 0.08);
      if ([0, 3, 8, 11, 14].includes(s16)) tone(t, hz(chordRoot - 12), 0.22, "sine", 0.3, { lp: 600 });
      if (lead && bar % 8 >= 2) pluck(t, lead);
      if (s16 === 0) keys(t, triad(chordRoot, minor), 1.8, 0.035);
      break;
    case "piano": // Amapiano: four-on-floor, shakers, log drum slides, piano stabs
      if (s16 % 4 === 0) kick(t, 0.7);
      shaker(t, s16 % 4 === 2 ? 0.1 : 0.05);
      if ([6, 14].includes(s16)) hit(t, 0.1, 0.12, "bandpass", 2400, 1);
      if ([3, 7, 10, 13].includes(s16)) logDrum(t, hz(chordRoot - (s16 === 13 ? 7 : 12)), 0.45);
      if ([0, 3, 6, 10].includes(s16)) keys(t, triad(chordRoot + 12, minor), 0.25, 0.04);
      if (lead && bar % 4 === 3) bell(t, lead, 0.05);
      break;
    case "lofi": // Lofi: lazy boom-bap, warm chords, sparse melody
      if ([0, 9].includes(s16)) kick(t, 0.6);
      if ([4, 12].includes(s16)) snare(t, 0.18);
      if (s16 % 2 === 0) hat(t, 0.05);
      if (s16 === 0) keys(t, [...triad(chordRoot, minor), chordRoot + 10], 3.2, 0.045);
      if (s16 === 0) tone(t, hz(chordRoot - 12), 1.6, "sine", 0.22, { lp: 400 });
      if (lead && s16 % 4 === 2) tone(t, lead, 0.5, "sine", 0.06, { attack: 0.03, lp: 1800 });
      break;
    case "trap": // Trap: half-time snare, 808 glides, hat rolls, dark bells
      if ([0, 10].includes(s16) || (s16 === 7 && bar % 2)) kick(t, 0.75);
      if (s16 === 8) snare(t, 0.32);
      hat(t, 0.06);
      if (bar % 4 === 3 && s16 >= 12) hat(t + (60 / st.bpm / 8), 0.05); // rolls
      if ([0, 10].includes(s16)) tone(t, hz(chordRoot - 24 + 12), 0.9, "sine", 0.45, { glideTo: s16 === 10 ? hz(chordRoot - 12 + 2) : undefined });
      if (lead && s16 % 2 === 0) bell(t, lead, 0.06);
      break;
    case "highlife": // Highlife: bright guitar arpeggios, congas, walking bass
      if ([0, 8].includes(s16)) kick(t, 0.6);
      if ([4, 12].includes(s16)) rim(t, 0.2);
      if ([2, 6, 10, 14].includes(s16)) tone(t, 220 + (s16 % 4) * 30, 0.12, "sine", 0.15, { drop: 1.3 });
      shaker(t, 0.04);
      pluck(t, hz(triad(chordRoot + 12, false)[s16 % 3] + (s16 % 6 > 2 ? 12 : 0)), 0.09);
      if (s16 % 4 === 0) tone(t, hz(chordRoot - 12 + [0, 4, 7, 4][(s16 / 4) | 0]), 0.3, "triangle", 0.25, { lp: 700 });
      break;
  }
  if (s16 === 15 && bar + 1 >= song.bars) {
    step = -1; // next step starts a new song
    newSong();
  }
}

function tick() {
  if (!ctx || !master) return;
  const st = STATIONS[radio.value.station];
  const sixteenth = 60 / st.bpm / 4;
  while (nextT < ctx.currentTime + 0.12) {
    // A little swing on the off-beats, like a human.
    const swing = step % 2 ? sixteenth * (st.genre === "lofi" ? 0.12 : 0.06) : 0;
    playStep(nextT + swing, Math.max(0, step));
    step++;
    nextT += sixteenth;
  }
}

function startAudio() {
  const c = ensure();
  if (!c || !master) return;
  void c.resume();
  if (!timer) {
    nextT = c.currentTime + 0.25;
    step = 0;
    timer = setInterval(tick, 25);
  }
  if (STATIONS[radio.value.station].genre === "lofi") startCrackle();
  else stopCrackle();
  master.gain.setTargetAtTime(0.5 * uiPrefs.value.volume, c.currentTime, 0.15);
  duckMusic(true);
}

function stopAudio() {
  if (timer) clearInterval(timer);
  timer = null;
  stopCrackle();
  if (ctx && master) master.gain.setTargetAtTime(0, ctx.currentTime, 0.1);
  duckMusic(false);
}

function startCrackle() {
  if (!ctx || crackle) return;
  const b = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() < 0.0006 ? Math.random() * 2 - 1 : (Math.random() * 2 - 1) * 0.015;
  crackle = ctx.createBufferSource();
  crackle.buffer = b;
  crackle.loop = true;
  const g = ctx.createGain();
  g.gain.value = 0.25;
  crackle.connect(g).connect(master!);
  crackle.start();
}
function stopCrackle() {
  crackle?.stop();
  crackle = null;
}

function staticBurst() {
  if (!ctx || !master) return;
  hit(ctx.currentTime + 0.01, 0.28, 0.25, "bandpass", 2500, 0.3);
}

const playing = () => radio.value.on && inCar;
const refresh = () => (playing() ? startAudio() : stopAudio());

// ---------------------------------------------------------------- public API
/** Call from a user gesture (button / key) the first time, so the browser allows audio. */
export function toggleRadio(on = !radio.value.on) {
  radio.value = { ...radio.value, on };
  if (on && !radio.value.title) newSong();
  save();
  if (on) staticBurst();
  refresh();
}

export function tuneRadio(dir: 1 | -1 = 1) {
  const station = (radio.value.station + dir + STATIONS.length) % STATIONS.length;
  radio.value = { ...radio.value, station, on: true };
  save();
  newSong();
  step = 0;
  if (ctx) nextT = ctx.currentTime + 0.3;
  staticBurst();
  refresh();
}

/** The game tells the radio when you get in / out of a vehicle. */
export function setInCar(v: boolean) {
  if (v === inCar) return;
  inCar = v;
  if (v && radio.value.on && !radio.value.title) newSong();
  refresh();
}

/** Live market lines for the DJ ("$MOWL up 340% today..."). */
export function setRadioNews(fn: () => string[]) {
  news = fn;
}

export function setRadioVolume() {
  if (ctx && master && playing()) master.gain.setTargetAtTime(0.5 * uiPrefs.value.volume, ctx.currentTime, 0.1);
}

// Restore the last station (it stays silent until you're in a vehicle).
{
  const s = saved();
  radio.value = { ...radio.value, on: s.on, station: s.station };
}
